import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Button,
  Grid,
  CircularProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  Article as ArticleIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import SectionHeader from '@shared/SectionHeader';
import FilterBar, { FilterOption } from '@shared/FilterBar';
import PaginatedList from '@shared/PaginatedList';
import EmptyState from '@shared/EmptyState';
import tipsService, { Tip, TipCategory } from '@services/tipsService';

const TipsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tips, setTips] = useState<Tip[]>([]);
  const [categories, setCategories] = useState<TipCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Paginare
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 12;

  // Filtre
  const [filters, setFilters] = useState({
    categoryId: '',
    categorySlug: '',
    search: '',
    animalType: '',
    isFeatured: undefined as boolean | undefined
  });

  // Încarcă categoriile
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await tipsService.getCategories();
        setCategories(cats);
      } catch (err) {
        console.error('Error loading categories:', err);
      }
    };
    loadCategories();
  }, []);

  // Încarcă tips-urile
  const loadTips = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const apiFilters: any = {
        page: currentPage,
        limit: itemsPerPage
      };

      if (filters.categoryId) apiFilters.categoryId = filters.categoryId;
      if (filters.categorySlug) apiFilters.categorySlug = filters.categorySlug;
      if (filters.search) apiFilters.search = filters.search;
      if (filters.animalType) apiFilters.animalType = filters.animalType;
      if (filters.isFeatured !== undefined) apiFilters.isFeatured = filters.isFeatured;

      const result = await tipsService.getTips(apiFilters);
      
      setTips(result.data);
      setTotalItems(result.total);
      setTotalPages(result.pagination.totalPages || Math.ceil(result.total / itemsPerPage));
    } catch (err: any) {
      console.error('Error loading tips:', err);
      setError(err.message || 'Eroare la încărcarea articolelor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTips();
  }, [filters, currentPage]);

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleFilterClear = () => {
    setFilters({
      categoryId: '',
      categorySlug: '',
      search: '',
      animalType: '',
      isFeatured: undefined
    });
    setCurrentPage(1);
  };

  const handleTipClick = (tip: Tip) => {
    navigate(`/tips/${tip.slug}`);
  };

  const filterOptions: FilterOption[] = [
    {
      key: 'categoryId',
      label: 'Categorie',
      type: 'select',
      options: [
        { value: '', label: 'Toate categoriile' },
        ...categories.map(cat => ({ value: cat.id, label: cat.name }))
      ]
    },
    {
      key: 'search',
      label: 'Caută',
      type: 'text',
      placeholder: 'Caută în articole...'
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <SectionHeader
        title="Tips & Tricks"
        subtitle="Ghiduri utile și recomandări pentru îngrijirea animalelor tale"
        onRefresh={loadTips}
        loading={loading}
      />

      {/* Filtre */}
      <FilterBar
        filters={filterOptions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleFilterClear}
        collapsible
      />

      {/* Lista tips-urilor */}
      <PaginatedList
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        loading={loading}
        error={error || undefined}
        emptyMessage="Nu există articole disponibile"
      >
        <Grid container spacing={3}>
          {tips.map((tip) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={tip.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => handleTipClick(tip)}
              >
                {tip.cover_image && (
                  <CardMedia
                    component="img"
                    height="200"
                    image={tip.cover_image}
                    alt={tip.title}
                    sx={{ objectFit: 'cover' }}
                  />
                )}
                
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', mb: 1 }}>
                    <Chip
                      label={tip.category_name || 'General'}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    {tip.is_featured && (
                      <StarIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                    )}
                  </Box>

                  <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                    {tip.title}
                  </Typography>

                  {tip.excerpt && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                      {tip.excerpt.length > 150 ? `${tip.excerpt.substring(0, 150)}...` : tip.excerpt}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
                    <Typography variant="caption" color="text.secondary">
                      {tip.views_count} vizualizări
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTipClick(tip);
                      }}
                    >
                      Citește mai mult
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </PaginatedList>
    </Container>
  );
};

export default TipsPage;

